import { morph } from "@theme/morph";
import { Component } from "@theme/component";
import {
  CartUpdateEvent,
  ThemeEvents,
  VariantSelectedEvent,
} from "@theme/events";
import { DialogComponent, DialogCloseEvent } from "@theme/dialog";
import {
  mediaQueryLarge,
  isMobileBreakpoint,
  getIOSVersion,
} from "@theme/utilities";
import {
  createQuickViewTrackingGate,
  getQuickViewTrackingContext,
} from "./quick-add-tracking.js";

/**
 * Checks the first available size in the rendered (already sorted) option order.
 * Shopify's own default, the card's variant and ?variant= all follow the unsorted variant order,
 * so the size option is always re-picked from the sorted list.
 * @param {Element} modalContent - The modal content element
 */
function selectFirstAvailableVariant(modalContent) {
  const inputs = /** @type {HTMLInputElement[]} */ ([
    ...modalContent.querySelectorAll(
      "fieldset[data-size-option] input[type=\"radio\"][data-variant-id]",
    ),
  ]);
  // All sizes sold out: fall back to the first size in the sorted list
  const target =
    inputs.find((input) => input.dataset.optionAvailable !== "false") ??
    inputs[0];
  if (!target || target.checked) return;

  target.checked = true;
  target.dispatchEvent(new Event("change", { bubbles: true }));
}

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  #quickViewTrackingGate = createQuickViewTrackingGate();

  get productPageUrl() {
    const productCard =
      /** @type {import('./product-card').ProductCard | null} */ (
        this.closest("product-card")
      );
    const hotspotProduct =
      /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
        this.closest("product-hotspot-component")
      );
    const productLink =
      productCard?.getProductCardLink() ||
      hotspotProduct?.getHotspotProductLink() ||
      this.closest("product-card")?.querySelector(
        "[data-bloomreach-product-link]",
      );

    if (!productLink?.href) return "";

    const url = new URL(productLink.href);
    url.searchParams.set("view", "quick-add");

    // No variant is forwarded from the card: the first available sorted size is picked after render
    url.searchParams.delete("variant");

    return url.toString();
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener("change", this.#closeQuickAddModal);
    document.addEventListener(
      ThemeEvents.variantSelected,
      this.#updateQuickAddButtonState.bind(this),
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener("change", this.#closeQuickAddModal);
    this.#abortController?.abort();
    document.removeEventListener(
      ThemeEvents.variantSelected,
      this.#updateQuickAddButtonState.bind(this),
    );
  }

  /**
   * Handles quick add button click.
   * @param {Event} event
   */
  handleClick = async (event) => {
    event.preventDefault();

    try {
      const html = await this.fetchProductPage(this.productPageUrl);
      const productGrid = html?.querySelector("[data-product-grid-content]");

      if (productGrid) {
        await this.updateQuickAddModal(
          /** @type {Element} */ (productGrid.cloneNode(true)),
        );
      }

      this.#openQuickAddModal();
    } catch (error) {
      console.error("[QuickAdd] handleClick failed", error);
    }
  };

  /**
   * Fetches and replaces the complete quick-add product content.
   * @param {string} productUrl
   * @param {string | null} [variantId]
   * @returns {Promise<void>}
   */
  async replaceProduct(productUrl, variantId = null) {
    const url = new URL(productUrl, window.location.origin);
    url.searchParams.set("view", "quick-add");
    if (variantId) url.searchParams.set("variant", variantId);

    const html = await this.fetchProductPage(url.toString());
    const productGrid = html?.querySelector("[data-product-grid-content]");
    if (!productGrid) return;

    const productTitle = productGrid.querySelector("h1")?.textContent?.trim();
    if (productTitle) this.dataset.productTitle = productTitle;

    await this.updateQuickAddModal(
      /** @type {Element} */ (productGrid.cloneNode(true)),
    );
  }

  #resetScroll() {
    const dialogComponent = document.getElementById("quick-add-dialog");
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector(".product-details");
    const productMedia = dialogComponent.querySelector(
      ".product-information__media",
    );
    productDetails?.scrollTo({ top: 0, behavior: "instant" });
    productMedia?.scrollTo({ top: 0, behavior: "instant" });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute("stay-visible", true);

    dialogComponent.addEventListener(
      DialogCloseEvent.eventName,
      () => {
        this.toggleAttribute("stay-visible", false);
        this.#quickViewTrackingGate.reset();
      },
      {
        once: true,
      },
    );
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById("quick-add-dialog");
    const dialog = dialogComponent?.querySelector("dialog");
    const dialogElement = dialog instanceof HTMLDialogElement ? dialog : null;
    console.log("[QuickAdd] open modal lookup", {
      component: dialogComponent,
      isQuickAddDialog: dialogComponent instanceof QuickAddDialog,
      dialog,
      dialogOpenBefore: dialogElement?.open,
      bodyPositionBefore: document.body.style.position,
    });
    if (!(dialogComponent instanceof QuickAddDialog)) {
      console.error("[QuickAdd] quick-add-dialog instance check failed", {
        constructor: dialogComponent?.constructor?.name,
        customElementDefined: customElements.get("quick-add-dialog")?.name,
      });
      return;
    }

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    try {
      dialogComponent.showDialog();
      dialogComponent.dispatchEvent(
        new Event("hm:pdp-countdown:refresh", { bubbles: true }),
      );
      this.#trackQuickView(dialogComponent);
      console.log("[QuickAdd] showDialog returned", {
        dialogOpenAfter: dialogElement?.open,
        hasOpenAttribute: dialogElement?.hasAttribute("open"),
        bodyPositionAfter: document.body.style.position,
        bodyTopAfter: document.body.style.top,
      });
    } catch (error) {
      console.error("[QuickAdd] showDialog threw", error);
      return;
    }

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialogRef = dialogComponent.querySelector(":scope > dialog");
    const dialogRefElement =
      dialogRef instanceof HTMLDialogElement ? dialogRef : null;
    console.log("[QuickAdd] direct dialog after showDialog", {
      dialogRef,
      dialogOpen: dialogRefElement?.open,
      dialogClass: dialogRefElement?.className,
    });
    if (!(dialogRef instanceof HTMLDialogElement)) {
      console.error("[QuickAdd] direct dialog is missing");
      return;
    }
    dialogRef.addEventListener("animationstart", this.#resetScroll.bind(this), {
      once: true,
    });
  };

  /** @param {QuickAddDialog} dialogComponent */
  #trackQuickView(dialogComponent) {
    const trackingContext = getQuickViewTrackingContext(dialogComponent);
    const reservation = this.#quickViewTrackingGate.reserve(trackingContext);
    if (!reservation) return;

    // Reuse the module instance loaded by theme.liquid. A bare "./bloomreach-tracking.js"
    // lacks the ?v= query of asset_url, so the browser would evaluate it a second time
    // and fire a duplicate page view and duplicate cart listeners.
    const trackingScript = document.querySelector(
      'script[type="module"][src*="bloomreach-tracking.js"]',
    );
    void import(trackingScript?.src ?? "./bloomreach-tracking.js")
      .then(({ getTrackingAdapter }) => {
        const adapter = getTrackingAdapter();
        if (!adapter) {
          reservation.release();
          return;
        }

        return reservation.track(() => adapter.trackQuickView(trackingContext));
      })
      .catch((error) => {
        reservation.release();
        console.warn("[Bloomreach] Quick view tracking failed:", error);
      });
  }

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById("quick-add-dialog");
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      console.log("[QuickAdd] fetch start", productPageUrl);
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });
      console.log("[QuickAdd] fetch response", {
        url: response.url,
        status: response.status,
        ok: response.ok,
        contentType: response.headers.get("content-type"),
      });

      if (!response.ok) {
        throw new Error(
          `Failed to fetch product page: HTTP error ${response.status}`,
        );
      }

      const responseText = await response.text();
      console.log("[QuickAdd] fetch body", {
        length: responseText.length,
        hasQuickAddGrid: responseText.includes("data-product-grid-content"),
      });
      const html = new DOMParser().parseFromString(responseText, "text/html");

      return html;
    } catch (error) {
      if (error.name === "AbortError") {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById("quick-add-modal-content");
    console.log("[QuickAdd] update modal lookup", {
      productGrid,
      modalContent,
      modalContentChildren: modalContent?.childElementCount,
    });

    if (!productGrid || !modalContent) {
      console.error("[QuickAdd] update modal skipped", {
        hasProductGrid: Boolean(productGrid),
        hasModalContent: Boolean(modalContent),
      });
      return;
    }

    console.log("[QuickAdd] before morph", {
      modalContentChildren: modalContent.childElementCount,
      productGridChildren: productGrid.childElementCount,
    });
    morph(modalContent, productGrid);
    modalContent.dispatchEvent(
      new Event("hm:pdp-countdown:refresh", { bubbles: true }),
    );
    console.log("[QuickAdd] after morph", {
      modalContentChildren: modalContent.childElementCount,
      modalContentHtml: modalContent.innerHTML.slice(0, 300),
    });

    selectFirstAvailableVariant(modalContent);
  }

  /**
   * Updates the quick-add button state based on whether a swatch is selected
   * @param {VariantSelectedEvent} event - The variant selected event
   */
  #updateQuickAddButtonState(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest("product-card") !== this.closest("product-card"))
      return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === "1" ? "add" : "choose";
    this.setAttribute("data-quick-add-button", quickAddButton);
  }
}

if (!customElements.get("quick-add-component")) {
  customElements.define("quick-add-component", QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, {
      signal: this.#abortController.signal,
    });
    this.addEventListener(
      ThemeEvents.variantUpdate,
      this.#updateProductTitleLink,
    );
    this.addEventListener(
      ThemeEvents.variantUpdate,
      this.#replaceChangedProduct,
      { capture: true },
    );

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(
      DialogCloseEvent.eventName,
      this.#handleDialogClose,
    );
    this.removeEventListener(
      ThemeEvents.variantUpdate,
      this.#replaceChangedProduct,
      { capture: true },
    );
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #replaceChangedProduct = async (/** @type {CustomEvent } */ event) => {
    const newProduct = event.detail?.data?.newProduct;
    if (!newProduct?.url) return;

    const url = new URL(newProduct.url, window.location.origin);
    url.searchParams.set("view", "quick-add");

    // No ?variant= here: the first available variant (sorted option order) is picked after the morph
    try {
      const response = await fetch(url);
      if (!response.ok)
        throw new Error(
          `Failed to fetch quick-add product: ${response.status}`,
        );

      const html = new DOMParser().parseFromString(
        await response.text(),
        "text/html",
      );
      const newProductGrid = html.querySelector("[data-product-grid-content]");
      const modalContent = this.querySelector("#quick-add-modal-content");

      if (!newProductGrid || !modalContent) return;

      morph(modalContent, newProductGrid.cloneNode(true));
      modalContent.dispatchEvent(
        new Event("hm:pdp-countdown:refresh", { bubbles: true }),
      );
      selectFirstAvailableVariant(modalContent);
    } catch (error) {
      if (error.name !== "AbortError")
        console.error("[QuickAdd] product replacement failed", error);
    }
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector(".view-product-title a")
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (
      this.querySelector(".view-product-title a")
    );
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (
      this.querySelector(".product-header a")
    );

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (
      !iosVersion ||
      iosVersion.major >= 17 ||
      (iosVersion.major === 16 && iosVersion.minor >= 4)
    )
      return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector("#ResultsList [product-grid-view]");
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = "";
        });
      }
    });
  };
}

if (!customElements.get("quick-add-dialog")) {
  customElements.define("quick-add-dialog", QuickAddDialog);
}
